/**
 * Tab values for the status tabs
 */
export type TabValue = "processing" | "processed";

/**
 * Document status IDs from the backend
 * These match the DocumentStatus enum in the Python backend
 */
export const DocumentStatusIds = {
  PENDING: 300,
  UPLOADED: 301,
  PROCESSING: 302,
  EXTRACTED: 303,
  REQUIRES_REVIEW: 304,
  CORRECTED: 305,
  COMPLETE: 306,
  FAILED: 307,
  DELETED: 308,
} as const;

/**
 * Status IDs for each tab
 * - processing: Documents being actively processed (PENDING, UPLOADED, PROCESSING, EXTRACTED)
 *   Note: EXTRACTED (303) is included because documents in this state are still going through
 *   the matching and validation pipeline steps before reaching a final processed state.
 * - processed: Documents that have been processed (REQUIRES_REVIEW, CORRECTED, COMPLETE, FAILED)
 */
export const TAB_STATUS_IDS: Record<TabValue, number[]> = {
  processing: [
    DocumentStatusIds.PENDING,
    DocumentStatusIds.UPLOADED,
    DocumentStatusIds.PROCESSING,
    DocumentStatusIds.EXTRACTED,
  ],
  processed: [
    DocumentStatusIds.REQUIRES_REVIEW,
    DocumentStatusIds.CORRECTED,
    DocumentStatusIds.COMPLETE,
    DocumentStatusIds.FAILED,
  ],
};

/**
 * Get status IDs for a given tab
 */
export function getStatusIdsForTab(tab: TabValue): number[] {
  return TAB_STATUS_IDS[tab];
}

interface StatusTabsProps {
  /** Currently active tab */
  activeTab: TabValue;
  /** Callback when tab changes */
  onTabChange: (tab: TabValue) => void;
  /** Count of documents in processing state */
  processingCount?: number;
  /** Count of documents in processed state */
  processedCount?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * StatusTabs component - Tab navigation for filtering documents by processing state
 *
 * Two tabs:
 * - "Currently Processing" - Shows documents with PENDING, UPLOADED, PROCESSING, EXTRACTED status
 *   (includes EXTRACTED because docs are still going through match/validate steps)
 * - "Processed Documents" - Shows documents with REQUIRES_REVIEW, CORRECTED, COMPLETE, FAILED status
 *
 * Optional count badges can be displayed on each tab.
 */
export function StatusTabs({
  activeTab,
  onTabChange,
  processingCount,
  processedCount,
  className = "",
}: StatusTabsProps) {
  return (
    <div
      className={`flex items-center border-b border-gray-200 dark:border-[#324467] ${className}`}
    >
      <TabButton
        label="Currently Processing"
        count={processingCount}
        isActive={activeTab === "processing"}
        onClick={() => onTabChange("processing")}
      />
      <TabButton
        label="Processed Documents"
        count={processedCount}
        isActive={activeTab === "processed"}
        onClick={() => onTabChange("processed")}
      />
    </div>
  );
}

interface TabButtonProps {
  label: string;
  count?: number;
  isActive: boolean;
  onClick: () => void;
}

function TabButton({ label, count, isActive, onClick }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        px-4 py-3 text-sm font-bold
        border-b-2 transition-colors duration-150
        ${
          isActive
            ? "border-primary text-primary"
            : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        }
      `}
    >
      {label}
      {count !== undefined && (
        <span
          className={`
            ml-2 px-2 py-0.5 text-xs font-bold rounded-full
            ${
              isActive
                ? "bg-primary/10 text-primary"
                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
            }
          `}
        >
          {count.toLocaleString()}
        </span>
      )}
    </button>
  );
}
